import { FC } from "react";
import { Button } from "react-bootstrap";
import { Pricesheet } from "@models/pricesheet.model";
import { PricesheetItem } from "@models/pricesheetitem.model";
import { formatCurrency } from "src/shared/utils/formatCurrency";
import { RecordId } from "@/shared/components/recordId/RecordId";

interface Props {
  pricesheets: readonly Pricesheet[];
  items: readonly PricesheetItem[];
  /** When provided, shows a "+ Add" button. Omit for read-only. */
  onCreate?: () => void;
  onEdit?: (pricesheet: Pricesheet) => void;
  onDelete?: (pricesheet: Pricesheet) => void;
}

const ContractPricesheetsPanel: FC<Props> = ({
  pricesheets,
  items,
  onCreate,
  onEdit,
  onDelete,
}) => {
  const editable = !!(onEdit || onDelete || onCreate);

  return (
    <div>
      {editable && (
        <div className="contract-section-header">
          <span className="section-hint">Pricing configuration for this contract</span>
          {onCreate && (
            <Button variant="primary" size="sm" onClick={onCreate}>
              <i className="bi bi-plus-circle me-1" />
              Add Pricesheet
            </Button>
          )}
        </div>
      )}

      {pricesheets.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <i className="bi bi-receipt fs-3 d-block mb-2 opacity-50" />
          <div className="small">No pricesheets configured</div>
        </div>
      ) : (
        pricesheets.map((ps) => {
          const psItems = items.filter((it) => it.pricesheet_id === ps.id);
          return (
            <PricesheetCard
              key={ps.id}
              pricesheet={ps}
              items={psItems}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          );
        })
      )}
    </div>
  );
};

interface CardProps {
  pricesheet: Pricesheet;
  items: PricesheetItem[];
  onEdit?: (ps: Pricesheet) => void;
  onDelete?: (ps: Pricesheet) => void;
}

function PricesheetCard({ pricesheet, items, onEdit, onDelete }: CardProps) {
  return (
    <div className="pricesheet-card">
      <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div>
            <div className="stat-label">Base price</div>
            <div className="stat-value">{formatCurrency(pricesheet.base_price)}</div>
          </div>
          <span
            className={`contract-status-badge ${pricesheet.is_active ? "active" : "inactive"}`}
          >
            {pricesheet.is_active ? "Active" : "Inactive"}
          </span>
        </div>
        {(onEdit || onDelete) && (
          <div className="d-flex gap-1">
            {onEdit && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onEdit(pricesheet)}
                title="Edit"
              >
                <i className="bi bi-pencil" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => onDelete(pricesheet)}
                title="Delete"
              >
                <i className="bi bi-trash" />
              </Button>
            )}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="mt-3">
          {groupItemsByPath(items).map((group) => (
            <div key={group.key} className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="fw-semibold" style={{ fontSize: "0.95rem" }}>
                  {group.name}
                  {/* The group IS a JourneyPath, so its id is pathId. Null for the
                      unassigned bucket, which is not a group and gets no id. */}
                  <RecordId label="Group" id={group.pathId} />
                </span>
                <span className="badge bg-secondary rounded-pill" style={{ fontSize: "0.7rem" }}>
                  {group.items.length}
                </span>
              </div>
              <div className="stat-label mb-1">Line items</div>
              <table className="table table-sm mb-0" style={{ fontSize: "0.82rem" }}>
                <thead>
                  <tr>
                    <th style={{ width: "55%" }}>Name</th>
                    <th style={{ width: "25%" }}>Step price</th>
                    <th style={{ width: "20%" }}>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{formatCurrency(item.step_price)}</td>
                      <td>{item.quantity ?? 1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface PathGroup {
  key: string;
  name: string;
  pathId: number | null;
  items: PricesheetItem[];
}

function groupItemsByPath(items: PricesheetItem[]): PathGroup[] {
  const byPath = new Map<number, PathGroup>();
  const unassigned: PricesheetItem[] = [];
  // Order by line-item sequence so steps render consistently with the audience
  // selector and badges (all keyed off PricesheetItem.sequence).
  const ordered = [...items].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  for (const item of ordered) {
    if (item.journey_path_id == null) {
      unassigned.push(item);
      continue;
    }
    const existing = byPath.get(item.journey_path_id);
    if (existing) {
      existing.items.push(item);
    } else {
      byPath.set(item.journey_path_id, {
        key: `path-${item.journey_path_id}`,
        name: item.journey_path_name || `Group #${item.journey_path_id}`,
        pathId: item.journey_path_id,
        items: [item],
      });
    }
  }
  const groups = Array.from(byPath.values());
  groups.sort((a, b) => a.name.localeCompare(b.name));
  if (unassigned.length > 0) {
    groups.push({
      key: "unassigned",
      name: "Unassigned",
      pathId: null,
      items: unassigned,
    });
  }
  return groups;
}

export default ContractPricesheetsPanel;
